import { Feature } from "geojson";

import { trafficLightData, TrafficLightValue } from "fond/map/Field/PreferenceFieldButton";

/**
 * Determines the preference classification for a feature based on its CostFactor property.
 *
 * - CostFactor < 1 = prefer
 * - CostFactor > 1 = avoid
 * - CostFactor === 1, null, or undefined = neutral
 */
export const getFeaturePreference = (feature: Feature): TrafficLightValue => {
  const raw = feature.properties?.CostFactor ?? feature.properties?.user_CostFactor;
  const costFactor = raw == null ? null : Number(raw);

  if (costFactor == null || Number.isNaN(costFactor) || costFactor === 1) {
    return TrafficLightValue.neutral;
  }

  if (costFactor < 1) {
    return TrafficLightValue.prefer;
  }

  return TrafficLightValue.avoid;
};

export interface PreferenceCounts {
  [TrafficLightValue.prefer]: number;
  [TrafficLightValue.neutral]: number;
  [TrafficLightValue.avoid]: number;
}

/**
 * Counts the number of features in each preference category.
 */
export const getPreferenceCounts = (features: Feature[]): PreferenceCounts => {
  const counts: PreferenceCounts = {
    [TrafficLightValue.prefer]: 0,
    [TrafficLightValue.neutral]: 0,
    [TrafficLightValue.avoid]: 0,
  };

  for (const feature of features) {
    counts[getFeaturePreference(feature)] += 1;
  }

  return counts;
};

/**
 * If all features share the same preference, returns its cost as a string.
 * Otherwise returns null.
 */
export const getUniformPreference = (counts: PreferenceCounts): string | null => {
  const nonZero = ([TrafficLightValue.prefer, TrafficLightValue.neutral, TrafficLightValue.avoid] as const).filter((pref) => counts[pref] > 0);
  if (nonZero.length === 1) {
    return trafficLightData[nonZero[0]].cost.toString();
  }
  return null;
};

export const getPathTypeCounts = (features: Feature[]): { paths: number; crossings: number } => {
  let paths = 0;
  let crossings = 0;

  for (const feature of features) {
    if (feature.properties?.Crossing === "YES") {
      crossings += 1;
    } else {
      paths += 1;
    }
  }

  return { paths, crossings };
};
